import {
  build_dashboard_stats,
  build_funnel_steps,
  fetch_all_onboarding_cases,
  fetch_onboarding_dashboard
} from '@/services/onboarding_journey_service';
import {
  resolve_onboarding_journey_filter
} from '@/services/onboarding_journey_filter_service';
import {
  normalize_text
} from '@/services/onboarding_data_helpers';

const create_empty_dashboard = (journey_label = 'All Journeys') => ({
  summary: {
    active_onboardings: 0,
    pending_approvals: 0,
    missing_documents: 0,
    high_risk: 0,
    ready_to_activate: 0,
    blocked: 0,
    journey_count: 0,
    document_blocker_count: 0
  },
  stats: [],
  funnel_steps: [],
  scope: {
    journey_label
  }
});

const build_document_summary = (case_item = {}) => {
  const documents = Array.isArray(case_item?.documents) ? case_item.documents.filter(Boolean) : [];
  const forms = Array.isArray(case_item?.forms) ? case_item.forms.filter(Boolean) : [];
  const visible_documents = documents.filter((document) => Boolean(
    document?.question
    || document?.file_name
    || document?.name
    || document?.document_name
    || document?.answer?.file_name
    || document?.answer?.presigned_url
    || document?.answer?.file_url
    || document?.presigned_url
    || document?.file_url
    || document?.url
    || document?.document_url
  ));

  if (visible_documents.length) {
    const unresolved_count = visible_documents.filter((document) => !document?.is_verified).length;
    return {
      has_document_work: unresolved_count > 0,
      unresolved_count
    };
  }

  return {
    has_document_work: forms.length > 0,
    unresolved_count: forms.length
  };
};

const get_case_journey_value = (case_item = {}) => (
  String(case_item?.journey_key || case_item?.journey_tone || case_item?.journey || '').trim()
);

const build_dashboard_from_cases = (cases = [], journey_label = 'All Journeys') => {
  const safe_cases = Array.isArray(cases) ? cases : [];
  const unique_journeys = new Set(
    safe_cases
      .map((case_item) => get_case_journey_value(case_item))
      .filter(Boolean)
  );
  const pending_approvals = safe_cases.filter((case_item) => case_item?.activation_tone === 'pending').length;
  const ready_to_activate = safe_cases.filter((case_item) => case_item?.activation_tone === 'ready').length;
  const blocked = safe_cases.filter((case_item) => case_item?.activation_tone === 'blocked').length;
  const high_risk = safe_cases.filter((case_item) => case_item?.risk_tone === 'high').length;
  const document_summaries = safe_cases.map((case_item) => build_document_summary(case_item));
  const document_blocker_count = document_summaries.filter((summary) => summary.has_document_work).length;
  const missing_documents = document_summaries.reduce((total, summary) => total + Number(summary.unresolved_count || 0), 0);

  return {
    summary: {
      active_onboardings: safe_cases.length,
      pending_approvals,
      missing_documents,
      high_risk,
      ready_to_activate,
      blocked,
      journey_count: unique_journeys.size,
      document_blocker_count
    },
    stats: build_dashboard_stats(safe_cases, journey_label),
    funnel_steps: build_funnel_steps(safe_cases),
    scope: {
      journey_label
    }
  };
};

const has_dashboard_content = (dashboard = {}) => (
  Array.isArray(dashboard?.stats) && dashboard.stats.length > 0
  && Array.isArray(dashboard?.funnel_steps) && dashboard.funnel_steps.length > 0
);

export default {
  name: 'OnboardingCommandCenter',
  data: () => ({
    is_loading_cases: false,
    cases_error: '',
    dashboard: create_empty_dashboard()
  }),
  computed: {
    active_journey_filter() {
      return resolve_onboarding_journey_filter(this.$route);
    },
    active_journey_label() {
      return this.active_journey_filter.label || 'All Journeys';
    },
    stats() {
      return Array.isArray(this.dashboard?.stats) ? this.dashboard.stats : [];
    },
    funnel_steps() {
      return Array.isArray(this.dashboard?.funnel_steps) ? this.dashboard.funnel_steps : [];
    },
    ready_count() {
      return Number(this.dashboard?.summary?.ready_to_activate || 0);
    },
    blocker_count() {
      return Number(this.dashboard?.summary?.blocked || 0);
    },
    active_count() {
      return Number(this.dashboard?.summary?.active_onboardings || 0);
    },
    avg_cycle_time() {
      return '-';
    },
    scope_caption() {
      const journey_count = Number(this.dashboard?.summary?.journey_count || 0);
      return this.active_journey_filter.id
        ? this.active_journey_label
        : `${journey_count || 0} journeys`;
    },
    hero_title() {
      return `${this.ready_count} onboardings ready to activate - ${this.blocker_count} blockers need you`;
    },
    hero_copy() {
      if (!this.active_count) {
        return `${this.active_journey_label} has no active onboarding records yet.`;
      }

      return `${this.active_journey_label} is showing live onboarding status, risk, documents and activation readiness.`;
    },
    funnel_scope_label() {
      return `${this.active_journey_label} - last 30 days`;
    }
  },
  created() {
    this.load_command_centre();
    window.addEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  beforeUnmount() {
    window.removeEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  methods: {
    async load_command_centre() {
      this.is_loading_cases = true;
      this.cases_error = '';

      try {
        const dashboard = await fetch_onboarding_dashboard({
          journey: this.active_journey_filter.id || '',
          journey_label: this.active_journey_label
        });
        if (has_dashboard_content(dashboard)) {
          this.dashboard = dashboard;
          return;
        }

        throw new Error('Dashboard payload was empty.');
      } catch (error) {
        try {
          const cases = await fetch_all_onboarding_cases({ include_runtime_progress: true });
          const normalized_journey = normalize_text(this.active_journey_filter.id);
          const scoped_cases = normalized_journey
            ? cases.filter((case_item) => (
              normalize_text(case_item?.journey_tone) === normalized_journey
              || normalize_text(case_item?.journey_key) === normalized_journey
              || normalize_text(String(case_item?.entity_type_id || '')) === normalized_journey
            ))
            : cases;

          this.dashboard = build_dashboard_from_cases(scoped_cases, this.active_journey_label);
          this.cases_error = '';
        } catch (fallback_error) {
          this.cases_error = fallback_error?.response?.data?.message
            || error?.response?.data?.message
            || fallback_error?.message
            || error?.message
            || 'Unable to load onboardings.';
          this.dashboard = create_empty_dashboard(this.active_journey_label);
        }
      } finally {
        this.is_loading_cases = false;
      }
    },
    handle_onboarding_data_updated() {
      this.load_command_centre();
    }
  }
};
